import React, { useEffect, useMemo, useRef, useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import { motion } from 'framer-motion';
import {
  Bold,
  Calendar,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Clock,
  FileText,
  GripVertical,
  Italic,
  MapPin,
  Plus,
  Printer,
  Shield,
  Trash2,
  Underline,
  Edit,
  ClipboardList,
  Sparkles,
} from 'lucide-react';

import PageHeader from '@/components/ui/PageHeader';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EmptyState from '@/components/ui/EmptyState';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { openBlobFileOnNative, isNativePlatform } from '@/utils/nativeAndroidFiles';

const EVENT_TYPE_OPTIONS = [
  'Culto de Celebração',
  'Culto de Domingo',
  'Culto de Oração',
  'Santa Ceia',
  'Conferência',
  'Evento Especial',
  'Reunião',
  'Outro',
];

const LOGO_FALLBACK = '/mbv-logo-512.png';

const RICH_TEXT_COMMANDS = [
  { command: 'bold', label: 'Negrito', icon: Bold },
  { command: 'italic', label: 'Italico', icon: Italic },
  { command: 'underline', label: 'Sublinhado', icon: Underline },
];

const createEmptyPlanItem = (index = 0) => ({
  id: `item-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 8)}`,
  title: '',
  description: '',
  time: '',
});

function parseDateOnly(value) {
  const normalized = String(value || '').slice(0, 10);
  const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const [, year, month, day] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDateLabel(value) {
  const date = parseDateOnly(value);
  return date
    ? date.toLocaleDateString('pt-BR', {
        weekday: 'long',
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      })
    : 'Data nao informada';
}

function reorderItems(list = [], startIndex, endIndex) {
  const next = [...list];
  const [removed] = next.splice(startIndex, 1);
  next.splice(endIndex, 0, removed);
  return next;
}

function escapeHtml(value) {
  return String(value || '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function normalizeWhitespace(value) {
  return String(value || '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\u00a0/g, ' ');
}

function convertPlainTextToHtml(value) {
  const escaped = escapeHtml(normalizeWhitespace(value)).replace(/\n/g, '<br />');
  return escaped;
}

function normalizeRichText(value) {
  const html = normalizeWhitespace(value).trim();
  if (!html) return '';
  const withoutTags = html.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim();
  return withoutTags ? html : '';
}

function getDisplayHtml(value) {
  const raw = normalizeWhitespace(value).trim();
  if (!raw) return '';
  const hasHtml = /<\/?[a-z][\s\S]*>/i.test(raw);
  return hasHtml ? raw : convertPlainTextToHtml(raw);
}

function getPlainTextPreview(value) {
  const raw = normalizeWhitespace(value).trim();
  if (!raw) return '';

  return raw
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/(p|div|li|ul|ol|h1|h2|h3|h4|h5|h6)>/gi, ' ')
    .replace(/<li>/gi, '• ')
    .replace(/<[^>]+>/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function RichTextEditor({ value, onChange, placeholder, disabled = false, minHeight = 120 }) {
  const editorRef = useRef(null);
  const htmlValue = getDisplayHtml(value);
  const isFocused = typeof document !== 'undefined' && document.activeElement === editorRef.current;

  useEffect(() => {
    if (!editorRef.current) return;
    if (isFocused) return;
    if (editorRef.current.innerHTML !== htmlValue) {
      editorRef.current.innerHTML = htmlValue;
    }
  }, [htmlValue, isFocused]);

  const applyCommand = (command) => {
    if (disabled) return;
    editorRef.current?.focus();
    document.execCommand(command, false);
    onChange(normalizeRichText(editorRef.current?.innerHTML || ''));
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
      <div className="flex items-center gap-1 border-b border-slate-200 bg-slate-50 px-2 py-2">
        {RICH_TEXT_COMMANDS.map(({ command, label, icon: Icon }) => (
          <Button
            key={command}
            type="button"
            variant="ghost"
            size="icon"
            disabled={disabled}
            title={label}
            className="h-8 w-8"
            onClick={() => applyCommand(command)}
          >
            <Icon className="w-4 h-4" />
          </Button>
        ))}
        <span className="ml-2 text-[11px] text-slate-500">Correcao ortografica ativa no navegador</span>
      </div>

      <div
        ref={editorRef}
        contentEditable={!disabled}
        suppressContentEditableWarning
        spellCheck
        autoCorrect="on"
        className="px-3 py-3 text-sm text-slate-700 focus:outline-none prose prose-sm max-w-none min-h-[120px]"
        style={{ minHeight }}
        data-placeholder={placeholder}
        onInput={(event) => onChange(normalizeRichText(event.currentTarget.innerHTML))}
      />
    </div>
  );
}

function buildPrintableHtml(plan, logoUrl) {
  const safeLogo = String(logoUrl || LOGO_FALLBACK).replace(/"/g, '&quot;');
  const notesHtml = getDisplayHtml(plan.notes);
  const itemsHtml = (plan.items || [])
    .map(
      (item, index) => `
        <div class="item">
          <div class="item-top">
            <div class="item-index">${index + 1}</div>
            <div class="item-main">
              <div class="item-header">
                <h3>${escapeHtml(item.title || 'Sem titulo')}</h3>
                ${item.time ? `<span class="time">${escapeHtml(item.time)}</span>` : ''}
              </div>
              ${item.description ? `<div class="item-content">${getDisplayHtml(item.description)}</div>` : '<p class="muted">Sem descricao.</p>'}
            </div>
          </div>
        </div>
      `
    )
    .join('');

  return `
    <!doctype html>
    <html lang="pt-BR">
      <head>
        <meta charset="UTF-8" />
        <title>${plan.title || 'Roteiro do culto'}</title>
        <style>
          @page { size: A4 portrait; margin: 10mm; }
          body {
            font-family: Arial, sans-serif;
            color: #111827;
            margin: 0;
            background: white;
          }
          .toolbar {
            position: sticky;
            top: 0;
            z-index: 3;
            display: flex;
            justify-content: flex-end;
            gap: 10px;
            padding: 14px 18px;
            background: rgba(255,255,255,0.96);
            border-bottom: 1px solid #e5e7eb;
          }
          .toolbar button {
            border: 0;
            border-radius: 999px;
            padding: 10px 16px;
            font-size: 13px;
            font-weight: 600;
            cursor: pointer;
          }
          .toolbar .print-btn {
            background: #1d4ed8;
            color: white;
          }
          .toolbar .close-btn {
            background: #e5e7eb;
            color: #111827;
          }
          .sheet {
            position: relative;
            overflow: hidden;
            padding: 10mm;
          }
          .watermark {
            position: fixed;
            inset: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            pointer-events: none;
            opacity: 0.06;
            z-index: 0;
          }
          .watermark img {
            width: 62%;
            max-width: 520px;
          }
          .content {
            position: relative;
            z-index: 1;
          }
          .header {
            border-bottom: 2px solid #111827;
            padding-bottom: 8px;
            margin-bottom: 12px;
          }
          .eyebrow {
            font-size: 11px;
            text-transform: uppercase;
            letter-spacing: 0.12em;
            color: #6b7280;
            margin-bottom: 6px;
          }
          h1 {
            margin: 0 0 4px;
            font-size: 22px;
          }
          .meta {
            display: flex;
            gap: 10px;
            flex-wrap: wrap;
            font-size: 11px;
            color: #374151;
          }
          .notes {
            margin: 10px 0 12px;
            background: #f9fafb;
            border: 1px solid #e5e7eb;
            border-radius: 12px;
            padding: 10px 12px;
            font-size: 11px;
            line-height: 1.4;
          }
          .items-grid {
            column-count: 2;
            column-gap: 12px;
          }
          .item {
            border: 1px solid #dbeafe;
            border-radius: 12px;
            padding: 10px;
            margin-bottom: 10px;
            background: rgba(255,255,255,0.94);
            break-inside: avoid;
            page-break-inside: avoid;
          }
          .item-top {
            display: flex;
            gap: 10px;
          }
          .item-index {
            width: 24px;
            height: 24px;
            border-radius: 999px;
            background: #111827;
            color: white;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: bold;
            flex-shrink: 0;
          }
          .item-main { flex: 1; }
          .item-header {
            display: flex;
            justify-content: space-between;
            gap: 12px;
            align-items: baseline;
          }
          .item-header h3 {
            margin: 0;
            font-size: 13px;
          }
          .time {
            font-size: 10px;
            font-weight: bold;
            color: #1d4ed8;
          }
          .item-content,
          .item p {
            margin: 6px 0 0;
            font-size: 10px;
            line-height: 1.35;
            color: #374151;
          }
          .item-content p { margin: 6px 0 0; }
          .item-content ul, .item-content ol { margin: 6px 0 0 16px; padding: 0; }
          .muted { color: #9ca3af; }
          @media print {
            .toolbar { display: none; }
            .sheet { padding: 0; }
          }
          @media screen and (max-width: 900px) {
            .items-grid { column-count: 1; }
          }
        </style>
      </head>
      <body>
        <div class="toolbar">
          <button class="close-btn" onclick="window.close()">Fechar</button>
          <button class="print-btn" onclick="window.print()">Imprimir</button>
        </div>
        <div class="sheet">
          <div class="watermark"><img src="${safeLogo}" alt="Logo MBV" /></div>
          <div class="content">
            <div class="header">
              <div class="eyebrow">Staff do Culto</div>
              <h1>${escapeHtml(plan.title || 'Roteiro do culto')}</h1>
              <div class="meta">
                <span>${escapeHtml(plan.event_type || 'Culto')}</span>
                <span>${formatDateLabel(plan.date)}</span>
                ${plan.start_time ? `<span>${escapeHtml(plan.start_time)}</span>` : ''}
                ${plan.location ? `<span>${escapeHtml(plan.location)}</span>` : ''}
              </div>
            </div>
            ${notesHtml ? `<div class="notes">${notesHtml}</div>` : ''}
            <div class="items-grid">${itemsHtml}</div>
          </div>
        </div>
      </body>
    </html>
  `;
}

export default function StaffCulto() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [viewingPlan, setViewingPlan] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});
  const [printingPlanId, setPrintingPlanId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    event_type: 'Culto de Celebração',
    date: '',
    start_time: '',
    location: '',
    notes: '',
    items: [createEmptyPlanItem(0)],
  });

  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => base44.auth.me(),
  });

  const { data: permissions } = useQuery({
    queryKey: ['staffPermissions', user?.email],
    queryFn: async () => {
      if (!user?.email) return null;
      const byUserEmail = await base44.entities.AdminPermission.filter({ user_email: user.email });
      if (byUserEmail.length > 0) return byUserEmail[0];
      const byEmail = await base44.entities.AdminPermission.filter({ email: user.email });
      return byEmail[0] || null;
    },
    enabled: !!user?.email,
  });

  const { data: plans = [], isLoading } = useQuery({
    queryKey: ['servicePlans'],
    queryFn: () => base44.entities.ServicePlan.list('-created_date', 500),
  });

  const { data: churchInfos = [] } = useQuery({
    queryKey: ['churchInfo-watermark'],
    queryFn: () => base44.entities.ChurchInfo.list(),
  });

  const isAdmin =
    user?.role === 'admin' ||
    permissions?.is_super_admin ||
    permissions?.can_manage_events ||
    permissions?.can_manage_church_info;

  const watermarkLogo = churchInfos?.[0]?.logo_url || LOGO_FALLBACK;

  const orderedPlans = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    const next = [...plans].sort((left, right) => {
      const leftDate = parseDateOnly(left.date);
      const rightDate = parseDateOnly(right.date);
      return (rightDate?.getTime() || 0) - (leftDate?.getTime() || 0);
    });

    return next.sort((left, right) => {
      const leftDate = parseDateOnly(left.date);
      const rightDate = parseDateOnly(right.date);
      const leftUpcoming = leftDate ? leftDate >= now : false;
      const rightUpcoming = rightDate ? rightDate >= now : false;
      if (leftUpcoming !== rightUpcoming) return leftUpcoming ? -1 : 1;
      return (leftDate?.getTime() || 0) - (rightDate?.getTime() || 0);
    });
  }, [plans]);

  const createMutation = useMutation({
    mutationFn: (payload) => base44.entities.ServicePlan.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['servicePlans'] });
      toast.success('Roteiro criado com sucesso!');
      resetForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.ServicePlan.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['servicePlans'] });
      toast.success('Roteiro atualizado com sucesso!');
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => base44.entities.ServicePlan.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['servicePlans'] });
      toast.success('Roteiro excluido com sucesso!');
    },
  });

  const resetForm = () => {
    setFormData({
      title: '',
      event_type: 'Culto de Celebração',
      date: '',
      start_time: '',
      location: '',
      notes: '',
      items: [createEmptyPlanItem(0)],
    });
    setEditingPlan(null);
    setExpandedItems({});
    setShowForm(false);
  };

  const handleEdit = (plan) => {
    const nextExpandedItems = {};
    (plan.items || []).forEach((item, index) => {
      const itemId = item.id || createEmptyPlanItem(index).id;
      nextExpandedItems[itemId] = index === 0;
    });

    setEditingPlan(plan);
    setFormData({
      title: plan.title || '',
      event_type: plan.event_type || 'Culto de Celebração',
      date: String(plan.date || '').slice(0, 10),
      start_time: plan.start_time || '',
      location: plan.location || '',
      notes: plan.notes || '',
      items: (plan.items || []).length
        ? plan.items.map((item, index) => ({
            id: item.id || createEmptyPlanItem(index).id,
            title: item.title || '',
            description: getDisplayHtml(item.description || ''),
            time: item.time || '',
          }))
        : [createEmptyPlanItem(0)],
    });
    setExpandedItems(nextExpandedItems);
    setShowForm(true);
  };

  const openCreateForm = () => {
    const firstItem = createEmptyPlanItem(0);
    setEditingPlan(null);
    setFormData({
      title: '',
      event_type: 'Culto de Celebração',
      date: '',
      start_time: '',
      location: '',
      notes: '',
      items: [firstItem],
    });
    setExpandedItems({ [firstItem.id]: true });
    setShowForm(true);
  };

  const handleSubmit = () => {
    if (!isAdmin) {
      toast.error('Somente o administrador pode alterar o roteiro.');
      return;
    }

    if (!formData.title || !formData.date) {
      toast.error('Titulo e data sao obrigatorios.');
      return;
    }

    const normalizedItems = (formData.items || [])
      .map((item, index) => ({
        id: item.id || createEmptyPlanItem(index).id,
        title: String(item.title || '').trim(),
        description: normalizeRichText(item.description),
        time: String(item.time || '').trim(),
      }))
      .filter((item) => item.title || item.description || item.time);

    if (normalizedItems.length === 0) {
      toast.error('Adicione pelo menos um item ao roteiro.');
      return;
    }

    const payload = {
      ...formData,
      items: normalizedItems,
    };

    if (editingPlan) {
      updateMutation.mutate({ id: editingPlan.id, data: payload });
      return;
    }

    createMutation.mutate(payload);
  };

  const handleDragEnd = (result) => {
    if (!isAdmin || !result.destination) return;
    const items = reorderItems(formData.items, result.source.index, result.destination.index);
    setFormData({ ...formData, items });
  };

  const updateItem = (itemId, field, value) => {
    if (!isAdmin) return;
    const items = formData.items.map((item) =>
      item.id === itemId ? { ...item, [field]: value } : item
    );
    setFormData({ ...formData, items });
  };

  const addItem = () => {
    if (!isAdmin) return;
    const newItem = createEmptyPlanItem(formData.items.length);
    setFormData({
      ...formData,
      items: [...formData.items, newItem],
    });
    setExpandedItems((current) => ({ ...current, [newItem.id]: true }));
  };

  const removeItem = (itemId) => {
    if (!isAdmin) return;
    const nextItems = formData.items.filter((item) => item.id !== itemId);
    const fallbackItem = createEmptyPlanItem(0);
    setFormData({
      ...formData,
      items: nextItems.length ? nextItems : [fallbackItem],
    });
    setExpandedItems((current) => {
      const next = { ...current };
      delete next[itemId];
      if (!nextItems.length) {
        next[fallbackItem.id] = true;
      }
      return next;
    });
  };

  const toggleItemExpanded = (itemId) => {
    if (!isAdmin) return;
    setExpandedItems((current) => ({ ...current, [itemId]: !current[itemId] }));
  };

  const expandAllItems = () => {
    if (!isAdmin) return;
    const nextExpandedItems = {};
    formData.items.forEach((item) => {
      nextExpandedItems[item.id] = true;
    });
    setExpandedItems(nextExpandedItems);
  };

  const collapseAllItems = () => {
    if (!isAdmin) return;
    const nextExpandedItems = {};
    formData.items.forEach((item) => {
      nextExpandedItems[item.id] = false;
    });
    setExpandedItems(nextExpandedItems);
  };

  const handlePrint = async (plan) => {
    setPrintingPlanId(plan.id);

    try {
      const printableHtml = buildPrintableHtml(plan, watermarkLogo);

      if (isNativePlatform()) {
        const printableBlob = new Blob([printableHtml], { type: 'text/html;charset=utf-8' });
        const safeTitle = String(plan.title || 'roteiro-culto')
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '') || 'roteiro-culto';

        await openBlobFileOnNative(printableBlob, `${safeTitle}.html`, 'text/html', {
          title: `Imprimir ${plan.title || 'roteiro do culto'}`,
          text: 'Abra o arquivo e use a opcao de imprimir do seu aparelho.',
        });
        toast.success('Arquivo de impressao aberto. Use a opcao de imprimir do seu aparelho.');
        return;
      }

      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        toast.error('Nao foi possivel abrir a janela de impressao.');
        return;
      }

      printWindow.document.write(printableHtml);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => printWindow.print(), 150);
    } catch (error) {
      console.error(error);
      toast.error('Nao foi possivel preparar a impressao.');
    } finally {
      setPrintingPlanId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      <div className="mbv-staff-header text-white pt-8 pb-6 px-4">
        <div className="max-w-5xl mx-auto">
          <PageHeader
            title="Staff do Culto"
            subtitle="Roteiro visual do culto e eventos especiais"
            showBack={true}
            action={
              isAdmin ? (
                <Button size="sm" className="bg-white text-slate-900 hover:bg-slate-100" onClick={openCreateForm}>
                  <Plus className="w-4 h-4 mr-1" />
                  Novo Roteiro
                </Button>
              ) : null
            }
          />
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-5">
        <div className="grid gap-4 md:grid-cols-[1.3fr_0.7fr]">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-2xl bg-blue-100 text-blue-700 flex items-center justify-center flex-shrink-0">
                <ClipboardList className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Roteiro do culto, do jeito prático</h2>
                <p className="text-sm text-slate-600 mt-1">
                  Monte a ordem do culto com horários, títulos e descrições, reorganize por arrastar e solte,
                  visualize com marca d&apos;água da igreja e imprima quando precisar.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
            <div className="flex items-start gap-4">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${isAdmin ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                {isAdmin ? <Sparkles className="w-6 h-6" /> : <Shield className="w-6 h-6" />}
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">{isAdmin ? 'Voce pode editar tudo' : 'Leitura liberada para todos'}</h2>
                <p className="text-sm text-slate-600 mt-1">
                  {isAdmin
                    ? 'Crie, ajuste a ordem, edite detalhes e imprima os roteiros.'
                    : 'Voce consegue abrir, acompanhar e imprimir os roteiros. As alteracoes ficam restritas ao administrador.'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {orderedPlans.length === 0 ? (
          <EmptyState
            icon={FileText}
            title="Nenhum roteiro criado"
            description={isAdmin ? 'Crie o primeiro roteiro do culto para organizar a ordem do evento.' : 'Ainda nao existe nenhum roteiro publicado.'}
            actionLabel={isAdmin ? 'Criar Roteiro' : undefined}
            onAction={isAdmin ? openCreateForm : undefined}
          />
        ) : (
          <div className="grid gap-4 lg:grid-cols-2">
            {orderedPlans.map((plan, index) => {
              const itemCount = Array.isArray(plan.items) ? plan.items.length : 0;
              return (
                <motion.div
                  key={plan.id}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.04 }}
                  className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden"
                >
                  <div className="mbv-staff-plan-banner p-5 text-white">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-2">
                          <Badge className="bg-white/15 text-white border-0">{plan.event_type || 'Culto'}</Badge>
                          <Badge className="bg-blue-400/20 text-blue-50 border-0">{itemCount} bloco(s)</Badge>
                        </div>
                        <h3 className="text-xl font-semibold break-words">{plan.title}</h3>
                        <div className="mt-3 flex flex-wrap gap-3 text-sm text-slate-100">
                          <span className="inline-flex items-center gap-1.5">
                            <Calendar className="w-4 h-4" />
                            {formatDateLabel(plan.date)}
                          </span>
                          {plan.start_time ? (
                            <span className="inline-flex items-center gap-1.5">
                              <Clock className="w-4 h-4" />
                              {plan.start_time}
                            </span>
                          ) : null}
                          {plan.location ? (
                            <span className="inline-flex items-center gap-1.5">
                              <MapPin className="w-4 h-4" />
                              {plan.location}
                            </span>
                          ) : null}
                        </div>
                      </div>
                      <Button size="icon" variant="secondary" className="bg-white/10 hover:bg-white/20 text-white border-0" onClick={() => setViewingPlan(plan)}>
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="p-5">
                    {plan.notes ? (
                      <p className="text-sm text-slate-600 mb-4 line-clamp-2">{plan.notes}</p>
                    ) : (
                      <p className="text-sm text-slate-400 mb-4">Sem observacoes gerais.</p>
                    )}

                    <div className="space-y-2">
                      {(plan.items || []).slice(0, 3).map((item, itemIndex) => (
                        <div key={item.id || itemIndex} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-slate-900 break-words">{item.title || 'Sem titulo'}</p>
                              {item.description ? (
                                <p className="text-xs text-slate-500 mt-1 line-clamp-2">{getPlainTextPreview(item.description)}</p>
                              ) : null}
                            </div>
                            {item.time ? <Badge variant="secondary" className="bg-blue-100 text-blue-700">{item.time}</Badge> : null}
                          </div>
                        </div>
                      ))}
                    </div>

                    {(plan.items || []).length > 3 ? (
                      <p className="text-xs text-slate-500 mt-3">+ {(plan.items || []).length - 3} item(ns) no roteiro</p>
                    ) : null}

                    <div className="flex flex-wrap gap-2 mt-5 pt-4 border-t border-slate-200">
                      <Button variant="outline" className="flex-1" onClick={() => setViewingPlan(plan)}>
                        <FileText className="w-4 h-4 mr-2" />
                        Visualizar
                      </Button>
                      <Button variant="outline" className="flex-1" onClick={() => handlePrint(plan)} disabled={printingPlanId === plan.id}>
                        <Printer className="w-4 h-4 mr-2" />
                        {printingPlanId === plan.id ? 'Preparando...' : 'Imprimir'}
                      </Button>
                      {isAdmin ? (
                        <>
                          <Button variant="outline" className="flex-1" onClick={() => handleEdit(plan)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Editar
                          </Button>
                          <Button
                            variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => {
                              if (confirm('Deseja excluir este roteiro?')) {
                                deleteMutation.mutate(plan.id);
                              }
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      ) : null}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={!!viewingPlan} onOpenChange={() => setViewingPlan(null)}>
        <DialogContent className="max-w-[95vw] lg:max-w-4xl max-h-[90vh] overflow-auto p-0">
          {viewingPlan ? (
            <div className="relative">
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-[0.05]">
                <img src={watermarkLogo} alt="Logo MBV" className="w-[58%] max-w-[420px]" />
              </div>

              <div className="relative p-6 lg:p-8 space-y-6">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-semibold text-slate-900">Staff do Culto</DialogTitle>
                </DialogHeader>

                <div className="mbv-staff-plan-banner text-white rounded-3xl p-6">
                  <div className="flex flex-wrap gap-2 mb-3">
                    <Badge className="bg-white/15 text-white border-0">{viewingPlan.event_type || 'Culto'}</Badge>
                    <Badge className="bg-blue-400/20 text-blue-50 border-0">{(viewingPlan.items || []).length} bloco(s)</Badge>
                  </div>
                  <h2 className="text-2xl lg:text-3xl font-semibold">{viewingPlan.title}</h2>
                  <div className="mt-4 flex flex-wrap gap-4 text-sm text-slate-100">
                    <span className="inline-flex items-center gap-1.5">
                      <Calendar className="w-4 h-4" />
                      {formatDateLabel(viewingPlan.date)}
                    </span>
                    {viewingPlan.start_time ? (
                      <span className="inline-flex items-center gap-1.5">
                        <Clock className="w-4 h-4" />
                        {viewingPlan.start_time}
                      </span>
                    ) : null}
                    {viewingPlan.location ? (
                      <span className="inline-flex items-center gap-1.5">
                        <MapPin className="w-4 h-4" />
                        {viewingPlan.location}
                      </span>
                    ) : null}
                  </div>
                </div>

                {viewingPlan.notes ? (
                  <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Observacoes gerais</p>
                    <div
                      className="prose prose-sm max-w-none text-slate-700 leading-snug"
                      dangerouslySetInnerHTML={{ __html: getDisplayHtml(viewingPlan.notes) }}
                    />
                  </div>
                ) : null}

                <div className="space-y-2 lg:space-y-0 lg:columns-2 lg:gap-3">
                  {(viewingPlan.items || []).map((item, index) => (
                    <div key={item.id || index} className="rounded-xl border border-blue-100 bg-white/95 shadow-sm p-3 mb-2 break-inside-avoid">
                      <div className="flex gap-3">
                        <div className="w-7 h-7 rounded-full bg-slate-900 text-white text-xs font-semibold flex items-center justify-center flex-shrink-0">
                          {index + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-start justify-between gap-3">
                            <div className="min-w-0">
                              <h3 className="text-sm font-semibold text-slate-900 break-words">{item.title || 'Sem titulo'}</h3>
                              {item.description ? (
                                <div
                                  className="prose prose-sm max-w-none text-xs text-slate-600 mt-1 leading-snug break-words"
                                  dangerouslySetInnerHTML={{ __html: getDisplayHtml(item.description) }}
                                />
                              ) : (
                                <p className="text-xs text-slate-400 mt-1">Sem descricao para este bloco.</p>
                              )}
                            </div>
                            {item.time ? <Badge className="bg-blue-100 text-blue-700 border-0 text-[10px]">{item.time}</Badge> : null}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex flex-col sm:flex-row gap-2 pt-2">
                  <Button variant="outline" className="flex-1" onClick={() => handlePrint(viewingPlan)} disabled={printingPlanId === viewingPlan.id}>
                    <Printer className="w-4 h-4 mr-2" />
                    {printingPlanId === viewingPlan.id ? 'Preparando...' : 'Imprimir'}
                  </Button>
                  {isAdmin ? (
                    <Button className="flex-1" onClick={() => {
                      handleEdit(viewingPlan);
                      setViewingPlan(null);
                    }}>
                      <Edit className="w-4 h-4 mr-2" />
                      Editar roteiro
                    </Button>
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={showForm && isAdmin} onOpenChange={(open) => setShowForm(isAdmin ? open : false)}>
        <DialogContent className="max-w-[95vw] lg:max-w-4xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>{editingPlan ? 'Editar roteiro' : 'Novo roteiro do culto'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-5 mt-2">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label>Titulo do culto ou evento *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Ex: Culto de Domingo - Noite"
                />
              </div>
              <div>
                <Label>Tipo</Label>
                <Select value={formData.event_type} onValueChange={(value) => setFormData({ ...formData, event_type: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {EVENT_TYPE_OPTIONS.map((option) => (
                      <SelectItem key={option} value={option}>{option}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <div>
                <Label>Data *</Label>
                <Input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} />
              </div>
              <div>
                <Label>Hora inicial</Label>
                <Input type="time" value={formData.start_time} onChange={(e) => setFormData({ ...formData, start_time: e.target.value })} />
              </div>
              <div>
                <Label>Local</Label>
                <Input value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} placeholder="Ex: Auditório principal" />
              </div>
            </div>

            <div>
              <Label>Observacoes gerais</Label>
              <RichTextEditor
                value={formData.notes}
                onChange={(value) => setFormData({ ...formData, notes: value })}
                placeholder="Informacoes importantes para toda a equipe."
                minHeight={110}
              />
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 lg:p-5">
              <div className="flex items-center justify-between gap-3 mb-4">
                <div>
                  <p className="text-sm font-semibold text-slate-900">Roteiro do culto</p>
                  <p className="text-xs text-slate-500">Arraste os blocos para definir a ordem ideal.</p>
                </div>
                <div className="flex flex-wrap items-center justify-end gap-2">
                  <Button type="button" variant="outline" onClick={expandAllItems}>
                    Expandir todos
                  </Button>
                  <Button type="button" variant="outline" onClick={collapseAllItems}>
                    Recolher todos
                  </Button>
                  <Button type="button" variant="outline" onClick={addItem}>
                    <Plus className="w-4 h-4 mr-2" />
                    Adicionar bloco
                  </Button>
                </div>
              </div>

              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="service-plan-items">
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-3">
                      {formData.items.map((item, index) => (
                        <Draggable key={item.id} draggableId={item.id} index={index}>
                          {(dragProvided) => (
                            <div
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex items-start gap-3 min-w-0">
                                  <div {...dragProvided.dragHandleProps} className="cursor-grab text-slate-400 hover:text-slate-700">
                                    <GripVertical className="w-5 h-5" />
                                  </div>
                                  <div className="min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <Badge variant="secondary" className="bg-slate-900 text-white">
                                        Bloco {index + 1}
                                      </Badge>
                                      {item.time ? <Badge variant="secondary" className="bg-blue-100 text-blue-700">{item.time}</Badge> : null}
                                    </div>
                                    <p className="text-sm font-semibold text-slate-900 mt-2 break-words">
                                      {item.title || 'Novo bloco'}
                                    </p>
                                    <p className="text-xs text-slate-500 mt-1 line-clamp-1">
                                      {getPlainTextPreview(item.description) || 'Toque para expandir e editar os detalhes deste bloco.'}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-1">
                                  <Button type="button" variant="ghost" size="icon" onClick={() => toggleItemExpanded(item.id)}>
                                    {expandedItems[item.id] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                  </Button>
                                  <Button type="button" variant="ghost" size="icon" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => removeItem(item.id)}>
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>

                              {expandedItems[item.id] ? (
                                <div className="mt-4">
                                  <div className="grid gap-4 md:grid-cols-[0.8fr_2.2fr]">
                                    <div>
                                      <Label>Horario</Label>
                                      <Input
                                        type="time"
                                        value={item.time}
                                        onChange={(e) => updateItem(item.id, 'time', e.target.value)}
                                      />
                                    </div>
                                    <div>
                                      <Label>Titulo do bloco</Label>
                                      <Input
                                        value={item.title}
                                        onChange={(e) => updateItem(item.id, 'title', e.target.value)}
                                        placeholder="Ex: Abertura, Oferta, Palavra"
                                      />
                                    </div>
                                  </div>

                                  <div className="mt-4">
                                    <Label>O que vai acontecer neste bloco</Label>
                                    <RichTextEditor
                                      value={item.description}
                                      onChange={(value) => updateItem(item.id, 'description', value)}
                                      placeholder="Descreva rapidamente o roteiro deste momento."
                                      minHeight={110}
                                    />
                                  </div>
                                </div>
                              ) : null}
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={resetForm}>
                Cancelar
              </Button>
              <Button
                className="flex-1"
                onClick={handleSubmit}
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {editingPlan ? 'Salvar roteiro' : 'Criar roteiro'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
